import { ExpandMore } from "@mui/icons-material"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Stack,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { MermaidDiagram } from "../MermaidDiagram"
import { panelSx } from "./Sl1ImportChrome"
import {
  SL1_APPLY_CHART,
  SL1_MATCH_KEY_CHART,
  SL1_RECON_CHART,
  SL1_STORAGE_CHART,
  SL1_TOPOLOGY_CHART,
} from "./sl1ImportFlowCharts"

/**
 * Reference documentation for the SL1 import, shown as the Doc tab of
 * /sa/sl1-import (LOCKER-382).
 *
 * Sections are collapsed by default and the tab sits LAST: this is reference
 * material for an operator who is about to run something, not a dashboard.
 *
 * ⚠ This prose and `./sl1ImportFlowCharts` are ONE surface and drift apart
 * silently — see that file's header for how exactly that has bitten before.
 * Change them together, and change them when the scripts change.
 *
 * Deliberately NOT translated through `t()` for the body copy. It is operator
 * documentation about code, quoting file paths and column names that do not
 * translate, and a half-translated runbook is worse than an untranslated one.
 * Only the tab's own headings go through i18n, matching
 * `Sstv2MigrationFlowDoc.tsx`.
 */

const Section: FC<{
  title: string
  subtitle: string
  chart?: string
  defaultExpanded?: boolean
  children: React.ReactNode
}> = ({ title, subtitle, chart, defaultExpanded = false, children }) => (
  <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ ...panelSx, mb: 1.5 }}>
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Stack>
        <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>{title}</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {subtitle}
        </Typography>
      </Stack>
    </AccordionSummary>
    <AccordionDetails>
      {chart && (
        // Diagrams scroll inside their own box — a wide flowchart must never make
        // the page body scroll sideways.
        <Box sx={{ overflowX: "auto", mb: 2 }}>
          <MermaidDiagram chart={chart} />
        </Box>
      )}
      <Stack spacing={1.5}>{children}</Stack>
    </AccordionDetails>
  </Accordion>
)

const P: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: "88ch" }}>
    {children}
  </Typography>
)

const Code: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="code"
    sx={{
      fontFamily: "monospace",
      fontSize: "0.82em",
      px: 0.5,
      py: 0.1,
      borderRadius: 0.5,
      bgcolor: "action.hover",
    }}
  >
    {children}
  </Box>
)

export const Sl1ImportFlowDoc: FC = () => {
  const { t } = useTranslation()

  return (
    <Stack data-ui="UI-SL2W-173">
      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <b>&quot;SL1&quot; here means the smart-locker feature in{" "}
          <Code>mobilus-webapp</Code></b> — not SirSteward API v1. The two are
          related only in that the webapp called the v1 API. Everything named{" "}
          <Code>sl1_locker_id</Code> or living under <Code>src/sstv2-evidence/</Code>{" "}
          uses the <i>other</i> sense.
        </Typography>
      </Alert>

      <Section
        title={t("sl1DocTopology")}
        subtitle={t("sl1DocTopologyHint")}
        chart={SL1_TOPOLOGY_CHART}
        defaultExpanded
      >
        <P>
          The webapp has <b>no locker-reservation table</b>. An SL1 reservation is
          the vendor&apos;s cached JSON response hanging off the order —{" "}
          <Code>orders.smart_locker_data_data</Code> — plus{" "}
          <Code>smart_locker_data_integration_order_id</Code> and the locker fields
          on the delivery address. The one thing the webapp <i>is</i> authoritative
          for is <Code>orders.date_created</Code>: the true creation date this
          whole operation exists to preserve.
        </P>
        <P>
          There are two scripts. <Code>sl1Recon.ts</Code> has two modes:{" "}
          <Code>--scan</Code> refreshes a cheap per-provider snapshot for the
          Overview tab, and <Code>--provider</Code> assesses one provider over a
          date window and produces a report. <b>Neither writes a reservation</b> —
          the recon is provably read-only, and that guarantee is what the apply&apos;s
          authorisation rests on.
        </P>
        <P>
          <Code>sl1Apply.ts</Code> is the other one, and it is{" "}
          <b>the only thing here that writes</b>. It takes an approved report, re-runs
          the recon&apos;s own reads and classifier, and refuses if the answer moved.
          It is a separate file rather than an <Code>--apply</Code> flag, deliberately:
          an import must not be reachable by a typo in a shell holding production
          credentials.
        </P>
        <P>
          The fleet row is a <b>snapshot, not a live query</b>. The alternative is
          a grouped count over <Code>orders</Code> — the biggest table in{" "}
          <Code>mobilus2</Code> — on every page load. The cost is staleness, which
          is why <Code>scannedAt</Code> is on the face of the Overview and why
          drift is computed rather than assumed zero.
        </P>
      </Section>

      <Section
        title={t("sl1DocRecon")}
        subtitle={t("sl1DocReconHint")}
        chart={SL1_RECON_CHART}
      >
        <P>
          The job record is created <b>first</b>, at <Code>RUNNING</Code>, so a run
          that dies half-way leaves a visible record rather than nothing. A recon
          that vanished is indistinguishable from one nobody started.
        </P>
        <P>
          Classification is a <b>pure</b> module with no AWS client, no database
          and no clock. That is not tidiness: it is what makes the read-only
          guarantee testable, and it is what lets the apply re-run the <i>same</i>
          classifier to check that an approved report still describes reality.
        </P>
        <P>
          A <b>short read is a stop, not a degradation.</b> If the locker walk or
          the reservation walk hits its page budget, the verdict cannot be{" "}
          <Code>clean</Code> — an incomplete index misclassifies rows in the
          direction that <i>duplicates</i> data, and a confident wrong answer is
          worse than an honest partial one.
        </P>
        <P>
          Every count on the report is computed once, in one place, and copied onto
          the job record. That is why the Job history tab renders with no S3 read
          at all, and why it keeps rendering after a report has aged out.
        </P>
      </Section>

      <Section
        title={t("sl1DocMatchKey")}
        subtitle={t("sl1DocMatchKeyHint")}
        chart={SL1_MATCH_KEY_CHART}
      >
        <P>
          A row matches SL2 on the <b>SST v1 reservation identity</b>, read out of
          the cached vendor response on the SL1 side and off{" "}
          <Code>sstReservation</Code> on the SL2 side. Exact match on the{" "}
          <Code>id</Code>; fallback on the <b>admin + reception + delivery codes</b>{" "}
          together.
        </P>
        <P>
          <b>The join in red is the whole correctness argument.</b> A reservation
          that was flipped or <i>adopted</i> during the v1→v2 migration keeps its v1
          object at <Code>preV2.sstReservation</Code> — and for the blank husks the
          flip minted (24,742 of them across the estate), that archive is their{" "}
          <i>only</i> SST v1 identity. Read just the live object and every husk goes
          unmatched, every SL1 row on that provider falls through to{" "}
          <Code>unmatched</Code>, and the plan duplicates the provider&apos;s
          entire history under a <Code>clean</Code> verdict.
        </P>
        <P>
          <b>
            <Code>integrationOrderId</Code> is not the key.
          </b>{" "}
          It is <Code>orders.smart_locker_data_integration_order_id</Code>, and it
          is not the same value as the reservation Thing&apos;s{" "}
          <Code>externalOrderId</Code> — which nothing in this ecosystem writes at
          all. It arrives only as caller-supplied API input from the
          provider&apos;s own system, so there was never a Mobilus identity on that
          side to join to. The id is still shown on every flagged row, as context.
        </P>
        <P>
          All three codes or none. A six-digit code on its own collides across a
          busy locker, and a fallback key that collides is a fallback that
          duplicates — so a partial triple is not treated as a key at all, and a
          row with neither an id nor a full triple is flagged{" "}
          <Code>noSstReservationIdentity</Code> rather than quietly called new.
        </P>
        <P>
          There is <b>no door and no locker dimension</b>. The webapp stores{" "}
          <Code>locker_integration_description</Code>, free text a human typed, not
          a door identifier — comparing it against SL2&apos;s partner{" "}
          <Code>doorId</Code> fired on essentially every row. And because the id
          and the codes come off the same vendor object on both sides, the key needs
          no vocabulary translation: the locker refs now feed{" "}
          <Code>lockerNotInSl2</Code> and nothing else.
        </P>
      </Section>

      <Section
        title={t("sl1DocApply")}
        subtitle={t("sl1DocApplyHint")}
        chart={SL1_APPLY_CHART}
      >
        <P>
          <b>Every written row&apos;s vendor object comes from a vendor.</b> That is
          the whole invariant. A husk replacement takes it from the husk — the{" "}
          <Code>adopt-</Code> row already carries the vendor&apos;s own view. A row
          with no husk gets it from a live <Code>GET /api/v1/reservations/:id</Code>.
          A row with neither is <b>not imported</b>:{" "}
          <Code>vendorNoLongerHasIt</Code>. Nothing is ever synthesised from the SL1
          blob.
        </P>
        <P>
          <b>Because the SL1 blob cannot describe a reservation.</b> Its{" "}
          <Code>status</Code> is a constant — <Code>PENDING_DELIVERY</Code> on every
          row that has one, estate-wide, across a population the husks independently
          prove ended <Code>CLOSE</Code> or <Code>DELETED</Code>. The webapp wrote it
          once at creation and never updated it. It is the <i>absence</i> of a
          status, not a status, and importing it would stamp 160k historical parcels
          as awaiting delivery.
        </P>
        <P>
          <b>An empty <Code>200</Code> is an absence, not a find.</b> SST v1&apos;s
          delete is a soft delete, so a reservation it no longer holds answers{" "}
          <Code>200</Code> with an empty body rather than <Code>404</Code>. Presence
          is therefore judged on the reservation identifying itself — the id echoing
          back — never on the status code. An error is neither: a 5xx or a network
          failure means <i>we do not know</i>, and the run aborts rather than reading
          a failed read as a deletion.
        </P>
        <P>
          <b>A replaced husk is tombstoned, not deleted.</b> The husk is the only
          record that the v1→v2 flip adopted anything, so the whole item is preserved
          under a different <Code>pk</Code>/<Code>sk</Code> with{" "}
          <b>every GSI key stripped</b>. Stripping the keys is the load-bearing half —
          an item in none of <Code>gsi1</Code>, <Code>gsi2</Code> or{" "}
          <Code>gsiSstRes</Code> is unreachable by every reservation access pattern
          while staying one <Code>GetItem</Code> away for an auditor. The put, the
          tombstone and the delete are <i>one transaction</i>: there is never a moment
          where the parcel has two rows or none.
        </P>
        <P>
          <b><Code>--dry-run</Code> shows exactly what <Code>--yes</Code> would do.</b>{" "}
          Every row is composed <i>before</i> the dry-run returns, and the writer then
          puts those same objects rather than a second composition that happens to
          agree today. It matters because the status guard is part of composition: the
          preview reports the rows it would refuse, with the status that refused them,
          instead of discovering them after the operator has committed. A refused row
          is not written <b>and its husk is not retired</b> — retiring it would destroy
          the only row representing that parcel.
        </P>
        <P>
          <b>A stale approval is refused.</b> The operator approves a report; the apply
          re-runs the same reads and the same classifier and compares{" "}
          <Code>planDigest</Code>, which covers both the bare inserts and the husk
          replacements. A differing digest means SL1 or SL2 moved since the report was
          produced, so the approval no longer describes what would happen.{" "}
          <Code>--accept-drift</Code> overrides it, loudly. A report from an older{" "}
          <Code>schemaVersion</Code> is refused outright — re-run the recon rather than
          reasoning about what an old report meant.
        </P>
      </Section>

      <Section
        title={t("sl1DocStorage")}
        subtitle={t("sl1DocStorageHint")}
        chart={SL1_STORAGE_CHART}
      >
        <P>
          A run stores three object families in its own bucket, split at{" "}
          <b>6 MB</b> — the same threshold and the same manifest shape the
          reservation-sync trail uses, because it is the same problem and two
          implementations would drift.
        </P>
        <P>
          <Code>report</Code> is the verdict, counts and anomaly table: a few
          kilobytes, and what the detail screen opens with.{" "}
          <Code>findings</Code> is one row per SL1 row, paged in on demand.{" "}
          <Code>snapshot</Code> is <b>the raw <Code>orders</Code> rows exactly as
          read</b>, before any classification.
        </P>
        <P>
          The snapshot is the one artefact that <b>cannot be re-derived</b>. The
          import preserves each reservation&apos;s original creation date, which
          makes it a one-way rewrite of history — and the webapp keeps writing
          while <Code>smart_locker_data_data</Code> is a mutable cache of a vendor
          response. A findings file records what the script <i>concluded</i>; the
          snapshot records what it <i>saw</i>. That is why this bucket retains for
          365 days where the reservation-sync store retains for 90.
        </P>
        <P>
          <b>The manifest is the security control.</b> A presigned URL is a bearer
          token for one object, so the endpoint will only sign a key that is listed
          in the run&apos;s own manifest, under a prefix carrying <i>both</i> the
          provider and the job id. Reading a <Code>snapshot</Code> part writes an
          admin-log entry, because those parts carry customer rows rather than
          derived counts.
        </P>
        <P>
          The browser fetches parts straight from S3 rather than through the API: a
          6 MB part would exceed API Gateway&apos;s response limit, and proxying it
          would put customer data through a Lambda with no reason to hold it.
        </P>
      </Section>

      <Section title={t("sl1DocNotBuilt")} subtitle={t("sl1DocNotBuiltHint")}>
        <P>
          <b>Two guards are still open, and they now matter</b> — rows are being
          written. The reminder cron scans the whole reservation partition and will
          pick up imported historical rows, and provider usage/billing counts by
          creation date with no exclusion for imported rows. Neither is gated by the
          import, so both are a reason to watch the first production applies rather
          than a reason to delay them.
        </P>
        <P>
          <b>The apply has no <Code>--all-tenants</Code> mode.</b> It runs one
          provider at a time, against one approved report. The estate is 167
          providers — 199,143 SL1 rows, of which 24,742 have a husk to replace and
          164,455 have none and must each be confirmed against SST v1 one{" "}
          <Code>GET</Code> at a time. Those 164,455 sit behind only 176 lockers, so
          the read is worth batching per locker before anyone attempts the estate;
          until then, a bulk run is hours of partner quota.
        </P>
        <P>
          The SL1↔SL2 link is <Code>Tenant.mblsProviderId</Code>, which equals{" "}
          <Code>integration_configuration.tenant_id</Code>. A run names the{" "}
          <i>SL2</i> tenant and the script resolves outward from it — never the
          reverse, because one provider id can be claimed by two SL2 tenants and
          something would then have to guess which tenant a provider&apos;s history
          belongs to. Absent or ambiguous, the run stops: the ambiguity is flagged
          on the Overview and nothing else is done. <Code>Needs mapping</Code>{" "}
          therefore remains a first-class state rather than an error.
        </P>
      </Section>
    </Stack>
  )
}

export default Sl1ImportFlowDoc
